/**
 * SG AGM 4종(B32201/B32202/B32203/B32204) 프리셋 스튜디오 — 문서 컴포넌트 공용 컨텍스트.
 *   폼 모델(기존 *FormModel)이 단일 원본. 문서의 [[변수]] 칸(CddVarField)은 패널이 useNoticeStudioSync 로 모델과 동기화하고,
 *   토글·목록(회원/퇴임이사/지정서명자)은 문서 컴포넌트가 이 컨텍스트로 모델을 직접 고친다.
 */
import { inject, provide, type InjectionKey, type Ref } from 'vue';
import type { SignerEntry } from '@/features/AI-documents/types/docGen.types';

/** 기존 생성 로직(build*PresetPrepareDocuments)이 정한 서명자 1명 */
export interface AgmAutoSigner { name: string; email?: string; role?: string; contactCd?: string }

export type AgmMemberType = 'multiple' | 'sole_corporate' | 'sole_individual';

/** 4종 모델의 공통 부분(문서 화면이 읽고 쓰는 필드). 모델별 추가 필드는 optional. */
export interface AgmStudioModel {
  memberType: AgmMemberType;
  members: Array<{ name: string; address?: string; contactCd?: string }>;
  retiringDirectors: Array<{ name: string }>;
  signers: Array<{ name: string }>;
  directorCount: number;
  specifySigners: boolean;
  hasRetiringDirectors: boolean;
  hasDirectorsRemuneration: boolean;
  isShortNotice?: boolean;
  isPartOfGroup?: boolean;
  [k: string]: any;
}

export interface AgmStudioCtx {
  model: AgmStudioModel;
  /** ✨ 인물 조회 — 기존 폼과 같은 fieldGroup/index 컨텍스트 */
  lookup: (ctx: Record<string, any>) => void;
  /** 문서 종류별 [[Signers]] 기본 서명자(생성 로직과 같은 규칙) */
  signerList: (docKind: string) => AgmAutoSigner[];
  /** 문서에서 사용자가 고친 서명자 — 키 `${docKind}_Signers`. 키가 있으면(빈 배열 포함) 생성 시 기본값 대신 사용 */
  docSigners: Record<string, SignerEntry[]>;
  /** SignersField 의 직책별 서명자 선택(Director/Shareholder…)용 */
  entityCd: Readonly<Ref<string>>;
  /** 회원 구성 확인 게이트(회사 선택 후 문서보다 먼저) — Final Dividends 와 같은 방식 */
  gate: {
    confirmed: Ref<boolean>;
    detected: Ref<'' | AgmMemberType>;
    summary: Ref<string[]>;
    loading: Ref<boolean>;
    loaded: Ref<boolean>;
  };
}

export const AGM_STUDIO_CTX: InjectionKey<AgmStudioCtx> = Symbol('sgAgmStudioCtx');
export function provideAgmStudio(ctx: AgmStudioCtx): void { provide(AGM_STUDIO_CTX, ctx); }
export function useAgmStudio(): AgmStudioCtx {
  const c = inject(AGM_STUDIO_CTX, null);
  if (!c) throw new Error('AGM studio context missing');
  return c;
}

/** signers JSON(문자열/배열) → 서명자 목록 */
export function signersFromVar(v: unknown): AgmAutoSigner[] {
  try {
    const arr = typeof v === 'string' ? (v ? JSON.parse(v) : []) : v;
    if (!Array.isArray(arr)) return [];
    return arr
      .map((s: any) => ({ name: String(s?.name || '').trim(), email: String(s?.email || '').trim(), role: String(s?.role || '').trim(), contactCd: String(s?.contactCd || '').trim() }))
      .filter((s) => s.name);
  } catch { return []; }
}
